// NewPlaces - Recently added places

import React, { useState, useEffect } from 'react';
import { Clock, ChevronLeft, ChevronRight, Sparkle } from 'lucide-react';
import type { Place } from '../../types';
import { getNewPlaces } from '../../services/placesService';
import { PlaceCard } from '../places/PlaceCard';

interface NewPlacesProps {
  favorites: string[];
  onSelectPlace: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
}

export function NewPlaces({
  favorites,
  onSelectPlace,
  onToggleFavorite,
}: NewPlacesProps) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollIndex, setScrollIndex] = useState(0);

  useEffect(() => {
    async function loadNew() {
      setIsLoading(true);
      try {
        const newPlaces = await getNewPlaces(8);
        setPlaces(newPlaces);
      } catch (error) {
        console.error('Error loading new places:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadNew();
  }, []);

  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex < Math.max(0, places.length - 4);

  const scrollLeft = () => {
    setScrollIndex((prev) => Math.max(0, prev - 1));
  };

  const scrollRight = () => {
    setScrollIndex((prev) => Math.min(places.length - 4, prev + 1));
  };

  // Helper to format relative time
  const getRelativeTime = (date: any): string => {
    if (!date) return '';

    const now = new Date();
    const placeDate = date.toDate ? date.toDate() : new Date(date);
    const diffInDays = Math.floor((now.getTime() - placeDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Heute';
    if (diffInDays === 1) return 'Gestern';
    if (diffInDays < 7) return `Vor ${diffInDays} Tagen`;
    if (diffInDays < 30) return `Vor ${Math.floor(diffInDays / 7)} Wochen`;
    return `Vor ${Math.floor(diffInDays / 30)} Monaten`;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-[20/19] bg-gray-200 rounded-xl mb-3" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (places.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-xl">
        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">
          Noch keine neuen Orte vorhanden.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Navigation Buttons */}
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/3 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-1/3 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Places Grid */}
      <div className="overflow-hidden">
        <div
          className="flex gap-4 transition-transform duration-300"
          style={{ transform: `translateX(-${scrollIndex * 25}%)` }}
        >
          {places.map((place) => (
            <div key={place.id} className="w-full sm:w-1/2 md:w-1/4 flex-shrink-0 relative">
              {/* New Badge */}
              {place.reviewCount === 0 && (
                <div className="absolute -top-2 -right-2 z-10 bg-green-500 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold shadow-lg">
                  <Sparkle className="w-3 h-3" />
                  Neu
                </div>
              )}
              <PlaceCard
                place={place}
                isFavorite={favorites.includes(place.id)}
                onSelect={onSelectPlace}
                onToggleFavorite={onToggleFavorite}
              />
              {/* Added date */}
              <p className="text-xs text-gray-400 mt-1 text-center">
                {getRelativeTime(place.createdAt)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <p className="text-xs text-gray-400 mt-4 text-center">
        Sei der Erste, der diese Orte bewertet!
      </p>
    </div>
  );
}

export default NewPlaces;
